import { AxiosError } from 'axios';

export interface ProcessedError {
  statusCode: number;
  message: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
  isAxiosError: boolean;
  originalError: unknown;
}

/**
 * Processes an unknown error input into a standardized ProcessedError object,
 * focusing on errors typically encountered in Redux thunks.
 * @param error - The error to process (unknown type).
 * @returns A ProcessedError object.
 */
export function processStoreError(error: unknown): ProcessedError {
  let statusCode: number = 0; // Default for non-HTTP or unknown errors
  let message: string = 'An unexpected error occurred. Please try again.';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let errorData: any | undefined;
  let isAxiosError = false;

  if (error instanceof AxiosError) {
    isAxiosError = true;
    statusCode = error.response?.status || 0; // Use 0 if no response/status
    // Prioritize Laravel's error message structure if available
    message = error.response?.data?.message || error.message || 'A server communication error occurred.';
    errorData = error.response?.data;
  } else if (error instanceof Error) {
    // Standard JavaScript Error
    ({ message } = error);
  } else if (typeof error === 'string' && error.trim() !== '') {
    // Plain string error
    message = error;
  } else if (error && typeof error === 'object') {
    // Attempt to extract message if it's a plain object with a message property
    if ('message' in error && typeof error.message === 'string' && error.message.trim() !== '') {
      ({ message } = error);
    }
    // Check for statusCode if it's a custom error object
    if ('statusCode' in error && typeof error.statusCode === 'number') {
      ({ statusCode } = error);
    }
    if ('data' in error) {
      errorData = error.data;
    }
  }

  return {
    statusCode,
    message,
    data: errorData,
    isAxiosError,
    originalError: error,
  };
}
